<template>
  <b-modal ref="AddPackagesModal"
           :no-close-on-backdrop="true" :no-enforce-focus="true" @hide="clearPackagesData">
    <div slot="modal-title">
      <div class="d-flex">
        <h5 class="modal-title">
          {{$t('modal_dialogs.add_packages')}}
        </h5>
      </div>
    </div>
    <div>
      <b-row>
        <b-col>
          <label>{{$t('labels.common.order')}}</label>
          <multiselect v-model="packagesData.order" :max-height="200"
                       placeholder="" label="descriptor" track-by="descriptor"
                       :options="salesOrders" :multiple="false"
                       :close-on-select="true"
                       select-label="" deselect-label="" selected-label=""
                       @select="selectSalesOrder"
          />
        </b-col>
      </b-row>
      <b-row class="d-flex justify-content-between mb-1">
        <b-col class="text-bold">
          {{$t('labels.sale_order.selected_packages')}}: {{packagesData.selectedPackagesCount}}
          {{$t('labels.common.m3')}}:
          {{packagesM3Count}}
        </b-col>
        <b-col class="d-flex justify-content-end">
          <toggle-button
            :width="50"
            @change="filterOperation"
            v-model="isOnlyFreePackages"
            :sync="true"
            class="mr-3"
            :color="{unchecked: 'rgb(228, 144, 144)', disabled: '#ccc'}"
            :disabled="!packagesData.order"
          />
          {{$t('labels.invoice.free_packages')}}
        </b-col>
      </b-row>
      <ag-grid-vue
        class="ag-theme-balham" :gridOptions="operationsGrid"
        :selectionChanged="selectionChangedOperations"
        :filterChanged="selectionChangedOperations" :postProcessPopup="postProcessPopup"
        :style="gridHeight" :rowData="operations" :columnDefs="operationsGridColumns"
      />
    </div>
    <div slot="modal-footer" class="w-100 d-flex justify-content-center align-items-center">
      <b-btn size="sm" class="ml-2" variant="success" @click="addPackages"
             title="Add packages" :disabled="!packagesData.order">
        {{$t('buttons.common.add')}}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
  import { mapState } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import { AgGridVue } from 'ag-grid-vue';

  import {
    addColIdToColumns,
    getArrayByProperty, getFilteredNodes,
    handlerResponse,
    sumTotalObjData
  } from '@/helpers/commonHelpers';
  import {
    addGroupingInResponseData,
    alignCells,
    autoResize,
    formatNumber, getNodeChildDetails, LinkCellRenderer,
    notificationsRenderer, ProgressBarInvoiced
  } from '@/helpers/renderHelpers';
  import gridProps from '@/gridProps';
  import { gridMixins } from '@/mixins';

  export default {
    mixins: [gridMixins],
    props: ['addPackagesToInvoiceCb'],
    components: {
      Multiselect,
      AgGridVue
    },
    data() {
      return {
        initialPackagesData: {
          order: null,
          packagesM3Count: {key: 'm3', value: 0},
          selectedPackagesCount: 0
        },
        packagesData: {},
        isOnlyFreePackages: false,
        gridHeight: {height: '375px'},
        operations: [],
        operationsGridColumns: [],
        operationsGrid: {
          rowSelection: 'multiple',
          getNodeChildDetails: getNodeChildDetails,
          groupSelectsChildren: true,
          components: {
            'InvoicedProgressBar': ProgressBarInvoiced('related_packets'),
            'OperationLink': LinkCellRenderer('id', '/operation/'),
            'InvoiceLink': LinkCellRenderer('invoice_item__invoice_id', '/sales-invoice/')
          },
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            enableRowGroup: true,
            valueFormatter(params) {
              return formatNumber(params.value);
            },
            cellStyle(params) {
              return alignCells(params);
            },
          },
          onRowDataChanged() {
            if (this.rowData && this.rowData.length) {
              autoResize(this);
            } else {
              this.api && this.api.hideOverlay();
              this.api && this.api.showNoRowsOverlay();
            }
          },
          onGridReady() {
            if (!this.rowData.length) {
              this.api && this.api.showNoRowsOverlay();
            }
          },
          ..._.cloneDeep(gridProps)
        },
      }
    },
    computed: {
      ...mapState('woodware', ['salesOrders']),
      packagesM3Count() {
        const m3PackagesData = this.packagesData && this.packagesData.packagesM3Count ? this.packagesData.packagesM3Count : this.initialPackagesData.packagesM3Count;
        return m3PackagesData.value.toFixed(3);
      }
    },
    methods: {
      async addPackages() {
        const filteredPackages = getFilteredNodes(this.operationsGrid)
          .filter(node => !node.group && node.selected)
          .map(node => node.data);
        const ids = getArrayByProperty(filteredPackages, 'id');
        if (ids.length !== 0) {
          await this.addPackagesToInvoiceCb(ids);
          await this.selectSalesOrder(this.packagesData.order);
        }
      },
      async selectSalesOrder(order) {
        const orderId = order && order.id;
        try {
          const response = await this.$http.get(`sales/salesorders/${orderId}/movements/`).then(handlerResponse);
          this.operations = addGroupingInResponseData(response.data).map(item => {
            if (item.related_packets && item.related_packets.length) {
              item.children = item.related_packets
            }
            return item;
          });
          this.operationsGridColumns = addColIdToColumns(response.used_columns);
          this.isOnlyFreePackages = false;
          this.packagesData.selectedPackagesCount = 0;
          this.packagesData.packagesM3Count = {key: 'm3', value: 0};
        } catch (e) {
          notificationsRenderer(e.data, 'error');
        }
      },
      selectionChangedOperations() {
        const selectedPackages = getFilteredNodes(this.operationsGrid)
          .filter(node => !node.group && node.selected)
          .map(node => node.data);
        this.packagesData.selectedPackagesCount = selectedPackages.length;
        this.packagesData.packagesM3Count = sumTotalObjData(selectedPackages, this.packagesData.packagesM3Count);
      },
      filterOperation() {
        const filterComponent = this.operationsGrid.api.getFilterInstance('invoice_item__invoice__invoice_number');
        filterComponent.selectNothing();
        let model = [];
        if (this.isOnlyFreePackages) {
          model = [null];
        } else {
          model = filterComponent.model.allUniqueValues;
        }
        filterComponent.setModel(model);
        this.operationsGrid.api.onFilterChanged();
      },
      clearPackagesData() {
        this.packagesData = {...this.initialPackagesData};
      },
    },
    beforeMount() {
      this.$store.dispatch('woodware/loadSalesOrders');
    },
    mounted() {
      this.$root.$on('openAddPackagesModal', _ => {
        if (this.$refs['AddPackagesModal']) {
          this.packagesData = {...this.initialPackagesData};
          this.isOnlyFreePackages = false;
          this.operations = [];
          this.operationsGridColumns = [];
          this.$refs['AddPackagesModal'].show();
        }
      });
    },
    beforeDestroy() {
      this.$root.$off('openAddPackagesModal');
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/modals/AddPackagesModal.vue